import React from "react";
import {
  Bot,
  Braces,
  Cloud,
  Code2,
  Database,
  Layers3,
  ServerCog,
  ShieldCheck,
  Smartphone,
  Workflow,
  Zap,
} from "lucide-react";

import TechStack from "../components/TechStack";

const technologyAreas = [
  {
    icon: Code2,
    title: "Frontend & UI",
    text:
      "React, Next.js, Tailwind CSS, Framer Motion and modern responsive UI technologies for building fast and intuitive SaaS interfaces.",
  },

  {
    icon: ServerCog,
    title: "Backend & API",
    text:
      "Node.js, Express.js, PHP, REST APIs, authentication and scalable backend architecture for business software and cloud applications.",
  },

  {
    icon: Smartphone,
    title: "Mobile Applications",
    text:
      "React Native and Expo for building connected Android and iOS applications integrated with cloud APIs and business platforms.",
  },

  {
    icon: Database,
    title: "Database & Data",
    text:
      "MongoDB, MySQL and structured data architectures for SaaS platforms, ERP, CRM, HRMS, billing systems and custom applications.",
  },

  {
    icon: Bot,
    title: "AI & Automation",
    text:
      "OpenAI, Gemini AI, Python and automation technologies for intelligent workflows, AI integrations and business process automation.",
  },

  {
    icon: Cloud,
    title: "Cloud & Infrastructure",
    text:
      "Cloudflare, AWS, Render, Docker and cloud deployment technologies for hosting, scaling and operating modern software platforms.",
  },

  {
    icon: Braces,
    title: "API & Integration",
    text:
      "REST APIs, third-party integrations, payment gateways and connected services that allow different business systems to work together.",
  },

  {
    icon: Workflow,
    title: "DevOps & Deployment",
    text:
      "Git-based development, automated deployment pipelines, environment management and production workflows for reliable software delivery.",
  },

  {
    icon: ShieldCheck,
    title: "Security & Authentication",
    text:
      "JWT authentication, role-based access, API security and security-conscious development practices for protecting applications and users.",
  },
];


const engineeringPrinciples = [
  {
    icon: Layers3,
    title: "Scalable Architecture",
    text:
      "Software architecture designed to support additional users, modules, integrations and evolving business requirements.",
  },

  {
    icon: Zap,
    title: "Performance Focused",
    text:
      "Frontend, backend and database workflows designed with application speed, usability and efficiency in mind.",
  },

  {
    icon: ShieldCheck,
    title: "Security Conscious",
    text:
      "Authentication, authorization, access control and data protection considered throughout the development lifecycle.",
  },

  {
    icon: Workflow,
    title: "Integration Ready",
    text:
      "Applications designed to connect with APIs, payment systems, databases, cloud services and third-party platforms.",
  },
];

export default function Technologies() {
  return (
    <main className="w-full overflow-hidden">

      <section className="relative w-full lg:w-[80%] mx-auto px-4 sm:px-6 py-16">

        <div className="pointer-events-none absolute left-1/2 top-10 -z-10 h-[450px] w-[750px] -translate-x-1/2 rounded-full bg-pink-600/10 blur-[150px]" />

        <div className="relative text-center max-w-4xl mx-auto mb-12">

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-pink-100 mb-5">

            <Code2 className="w-4 h-4 text-pink-300" />

            <span className="text-xs font-bold uppercase tracking-[0.2em]">
              OjMee Technology Stack
            </span>

          </div>


          <h1 className="text-4xl sm:text-6xl font-black leading-tight">

            Technologies Behind Our

            <span className="block bg-gradient-to-r from-pink-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
              SaaS & Business Software
            </span>

          </h1>


          <p className="text-slate-300 mt-5 text-base sm:text-lg leading-8">

            OjMee Technology uses modern frontend, backend, mobile,
            database, AI, cloud, API and DevOps technologies to build
            scalable SaaS products and connected business software.

          </p>


          <p className="text-slate-500 mt-3 text-sm sm:text-base leading-7 max-w-3xl mx-auto">

            Our technology stack supports the development of ERP, CRM,
            HRMS, billing and invoicing platforms, business applications,
            automation systems, dashboards and cloud-based software.

          </p>

        </div>

        <TechStack />

        <div className="mt-14 grid md:grid-cols-3 gap-5">

          {[
            {
              icon: Code2,
              title: "Frontend & UI",
              text:
                "React, Next.js, Tailwind CSS, Framer Motion and modern responsive UI systems for building fast and intuitive SaaS interfaces.",
            },

            {
              icon: ServerCog,
              title: "Backend & API",
              text:
                "Node.js, Express.js, PHP, REST APIs, secure authentication and scalable backend systems for cloud and business applications.",
            },

            {
              icon: Bot,
              title: "AI & Cloud",
              text:
                "OpenAI, Gemini AI, Python, Cloudflare, AWS, Docker and automation technologies for intelligent and scalable software platforms.",
            },
          ].map((item) => {
            const Icon = item.icon;

            return (
              <div
                key={item.title}
                className="group relative overflow-hidden rounded-3xl bg-white/[0.07] border border-white/10 p-6 transition duration-300 hover:-translate-y-1 hover:border-pink-400/30"
              >

                <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-pink-500/10 opacity-0 blur-[70px] transition duration-500 group-hover:opacity-100" />


                <div className="relative">

                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-pink-500/15 to-violet-500/15">

                    <Icon className="h-5 w-5 text-pink-300" />

                  </div>


                  <h2 className="text-2xl font-black text-pink-300 mt-5">
                    {item.title}
                  </h2>


                  <p className="text-slate-300 mt-3 leading-7">
                    {item.text}
                  </p>

                </div>

              </div>
            );
          })}

        </div>

      </section>


      <section className="relative w-full border-y border-white/5 bg-white/[0.015] px-4 py-16 sm:px-6 lg:py-20">

        <div className="mx-auto max-w-7xl">

          <div className="mx-auto max-w-4xl text-center">

            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-violet-400/20 bg-violet-400/5 px-4 py-2">

              <Layers3 className="h-4 w-4 text-violet-300" />

              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-200">
                Engineering Capabilities
              </span>

            </div>


            <h2 className="text-3xl font-black sm:text-5xl">

              A Modern Technology Stack for

              <span className="block bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                Complete Software Development
              </span>

            </h2>


            <p className="mx-auto mt-5 max-w-3xl text-sm leading-8 text-slate-500 sm:text-base">

              Different software problems require different technologies.
              We combine frontend, backend, cloud, mobile, databases,
              integrations and automation based on the requirements of
              each product.

            </p>

          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-3">

            {technologyAreas.map((item, index) => {
              const Icon = item.icon;

              return (
                <article
                  key={item.title}
                  className="group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.025] p-7 transition duration-300 hover:-translate-y-1 hover:border-pink-400/30 hover:bg-white/[0.04]"
                >

                  <div className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-violet-500/10 opacity-0 blur-[80px] transition duration-500 group-hover:opacity-100" />


                  <div className="relative">

                    <div className="flex items-start justify-between">

                      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-pink-500/15 to-violet-500/15 ring-1 ring-white/5">

                        <Icon className="h-6 w-6 text-pink-300" />

                      </div>


                      <span className="text-xs font-bold tracking-widest text-slate-700">

                        {String(index + 1).padStart(2, "0")}

                      </span>

                    </div>


                    <h3 className="mt-6 text-xl font-black text-white">
                      {item.title}
                    </h3>


                    <p className="mt-3 text-sm leading-7 text-slate-500">
                      {item.text}
                    </p>

                  </div>

                </article>
              );
            })}

          </div>

        </div>

      </section>

      <section className="w-full px-4 py-16 sm:px-6 lg:py-20">

        <div className="mx-auto max-w-7xl">

          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">

            <div>

              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-pink-400/20 bg-pink-400/5 px-4 py-2">

                <Braces className="h-4 w-4 text-pink-300" />

                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-200">
                  Our Engineering Approach
                </span>

              </div>


              <h2 className="text-3xl font-black sm:text-5xl">

                Technology Selected Around

                <span className="block bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                  Product Requirements
                </span>

              </h2>


              <p className="mt-6 text-sm leading-8 text-slate-500 sm:text-base">

                We do not select technology only because it is popular.
                The architecture and technology stack should support the
                users, workflows, integrations, performance and long-term
                requirements of the software product.

              </p>


              <p className="mt-4 text-sm leading-8 text-slate-500 sm:text-base">

                This approach allows us to build SaaS platforms and business
                applications that remain easier to maintain, integrate and
                evolve as requirements change.

              </p>

            </div>


            <div className="grid gap-5 sm:grid-cols-2">

              {engineeringPrinciples.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    className="rounded-[2rem] border border-white/10 bg-white/[0.025] p-6 transition duration-300 hover:-translate-y-1 hover:border-violet-400/30"
                  >

                    <div className="grid h-12 w-12 place-items-center rounded-2xl bg-violet-500/10">

                      <Icon className="h-5 w-5 text-violet-300" />

                    </div>


                    <h3 className="mt-5 text-lg font-black text-white">
                      {item.title}
                    </h3>


                    <p className="mt-3 text-sm leading-7 text-slate-500">
                      {item.text}
                    </p>

                  </div>
                );
              })}

            </div>

          </div>

        </div>

      </section>

      <section className="relative w-full border-y border-white/5 bg-white/[0.015] px-4 py-16 sm:px-6 lg:py-20">

        <div className="mx-auto max-w-7xl">

          <div className="mx-auto max-w-4xl text-center">

            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-violet-400/20 bg-violet-400/5 px-4 py-2">

              <Cloud className="h-4 w-4 text-violet-300" />

              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-200">
                SaaS Technology
              </span>

            </div>


            <h2 className="text-3xl font-black sm:text-5xl">

              Technology That Supports

              <span className="block bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                Modern Business Software
              </span>

            </h2>


            <p className="mx-auto mt-6 max-w-3xl text-sm leading-8 text-slate-500 sm:text-base">

              Our technology capabilities support cloud-based SaaS products,
              ERP and CRM platforms, HRMS systems, invoicing software,
              business dashboards, remote-access applications, mobile apps
              and custom operational software.

            </p>

          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-3">

            {[
              "SaaS Platforms",
              "ERP Software",
              "CRM Systems",
              "HRMS Platforms",
              "Billing & Invoicing",
              "Business Dashboards",
              "AI Automation",
              "Mobile Applications",
              "Cloud APIs",
              "Remote Access Software",
              "Custom Business Software",
            ].map((item) => (

              <div
                key={item}
                className="rounded-full border border-white/10 bg-white/[0.04] px-5 py-2.5 text-sm font-semibold text-slate-300 transition hover:border-pink-400/30 hover:text-pink-200"
              >
                {item}
              </div>

            ))}

          </div>

        </div>

      </section>

    </main>
  );
}